import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";
import {
  StepStrip,
  WrittenBoard,
  padDigits,
} from "../../../dzial1_liczby_i_dzialania/temat8_dzialania_pisemne/sharedWrittenNatural";
import { divisionRemainderCells } from "../../../dzial1_liczby_i_dzialania/temat8_dzialania_pisemne/writtenDivisionLayout";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(items) {
  return items[randInt(0, items.length - 1)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

function stripTrailingZeros(value) {
  return value.replace(/,?0+$/, "");
}

function formatDecimal(units, places) {
  const sign = units < 0 ? "-" : "";
  const raw = String(Math.abs(units)).padStart(places + 1, "0");
  if (places === 0) return `${sign}${raw}`;
  const intPart = raw.slice(0, raw.length - places);
  const fracPart = raw.slice(raw.length - places);
  return stripTrailingZeros(`${sign}${intPart},${fracPart}`);
}

function formatDecimalFixed(units, places) {
  const sign = units < 0 ? "-" : "";
  const raw = String(Math.abs(units)).padStart(places + 1, "0");
  if (places === 0) return `${sign}${raw}`;
  const intPart = raw.slice(0, raw.length - places);
  const fracPart = raw.slice(raw.length - places);
  return `${sign}${intPart},${fracPart}`;
}

function formatMathDecimal(value) {
  return String(value).replace(".", "{,}");
}

function fractionLatex(numerator, denominator) {
  if (denominator === 1) return `${numerator}`;
  return `\\frac{${numerator}}{${denominator}}`;
}

function MathText({ math, size = "1.35rem" }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1.15 }}>
      <InlineMath math={math} />
    </span>
  );
}

function normalizeFraction(numerator, denominator) {
  if (denominator < 0) return normalizeFraction(-numerator, -denominator);
  if (numerator === 0) return { numerator: 0, denominator: 1 };
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function multiplyFractions(a, b) {
  return normalizeFraction(a.numerator * b.numerator, a.denominator * b.denominator);
}

function reducedMultiplicationItems(fractions) {
  const items = fractions.map((fraction) => ({
    numerator: Math.abs(fraction.numerator),
    denominator: fraction.denominator,
    reducedNumerator: Math.abs(fraction.numerator),
    reducedDenominator: fraction.denominator,
  }));

  for (let i = 0; i < items.length; i += 1) {
    for (let j = 0; j < items.length; j += 1) {
      const divisor = gcd(items[i].reducedNumerator, items[j].reducedDenominator);
      if (divisor > 1) {
        items[i].reducedNumerator /= divisor;
        items[j].reducedDenominator /= divisor;
      }
    }
  }

  return items;
}

function reducedGroupProduct(items) {
  const fractions = items.map((item) => ({
    numerator: item.reducedNumerator,
    denominator: item.reducedDenominator,
  }));
  return fractions.slice(1).reduce((acc, fraction) => multiplyFractions(acc, fraction), fractions[0]);
}

function CancelledNumber({ value, reduced, placement = "top" }) {
  const changed = value !== reduced;
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        minWidth: 24,
        overflow: "visible",
      }}
    >
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <InlineMath math={`${value}`} />
        {changed ? (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: -1,
              right: -1,
              top: "50%",
              borderTop: "3px solid #f59e0b",
              transform: "translateY(-50%)",
            }}
          />
        ) : null}
      </span>
      {changed ? (
        <span
          style={{
            position: "absolute",
            left: "calc(100% + 2px)",
            top: placement === "top" ? -13 : "auto",
            bottom: placement === "bottom" ? -13 : "auto",
            color: "#f59e0b",
            fontSize: "0.72em",
            lineHeight: 1,
          }}
        >
          <InlineMath math={`${reduced}`} />
        </span>
      ) : null}
    </span>
  );
}

function CancellationFraction({ item }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2, fontSize: "1.2rem" }}>
      <CancelledNumber value={item.numerator} reduced={item.reducedNumerator} />
      <span style={{ width: "100%", minWidth: 34, borderTop: "2px solid currentColor" }} />
      <CancelledNumber value={item.denominator} reduced={item.reducedDenominator} placement="bottom" />
    </span>
  );
}

function CancellationProduct({ fractions }) {
  const items = reducedMultiplicationItems(fractions);
  const reducedProduct = reducedGroupProduct(items);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", margin: "12px 0" }}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 ? <span style={{ fontSize: "1.35rem" }}>·</span> : null}
          <CancellationFraction item={item} />
        </React.Fragment>
      ))}
      <span style={{ fontSize: "1.35rem" }}>=</span>
      <MathText
        math={fractionLatex(
          items.map((item) => item.reducedNumerator).join("\\cdot "),
          items.map((item) => item.reducedDenominator).join("\\cdot ")
        )}
      />
      <span style={{ fontSize: "1.35rem" }}>=</span>
      <MathText math={fractionLatex(reducedProduct.numerator, reducedProduct.denominator)} />
    </div>
  );
}

function decimalPlacesOf(text) {
  const [, frac = ""] = String(text).split(",");
  return frac.length;
}

function pow10(n) {
  return 10 ** n;
}

function placesText(count) {
  return (
    {
      1: "jedno miejsce",
      2: "dwa miejsca",
      3: "trzy miejsca",
      4: "cztery miejsca",
      5: "pięć miejsc",
    }[count] || `${count} miejsc`
  );
}

function naturalDivisionCheck(data) {
  if (!Number.isInteger(data.answer)) return null;
  return (
    <p>
      Sprawdzamy:{" "}
      <InlineMath math={`${data.naturalDivisor}\\cdot ${formatMathDecimal(data.answerText)}=${data.naturalDividend}`} />.
    </p>
  );
}

function shiftedDivisionData(data) {
  const shift = data.divisorPlaces || 0;
  return {
    dividendUnits: data.dividendUnits,
    dividendPlaces: Math.max(0, data.dividendPlaces - shift),
    divisor: data.divisorUnits,
    answerText: data.answerText,
  };
}

function analyzeDecimalWrittenDivision(source) {
  const dividendDigits = String(source.dividendUnits).split("").map(Number);
  const quotientDigitsOnly = source.answerText.replace(",", "").split("").map(Number);
  const integerDigitCount = Math.max(1, dividendDigits.length - source.dividendPlaces);
  const quotientDigits = Array(Math.max(dividendDigits.length, quotientDigitsOnly.length + 1)).fill(" ");
  const rows = [];
  const detailLines = [];
  const steps = [];
  let current = 0;
  let quotientIndex = 0;
  let remainder = 0;

  for (let idx = 0; quotientIndex < quotientDigitsOnly.length && idx < dividendDigits.length + 8; idx += 1) {
    const digit = idx < dividendDigits.length ? dividendDigits[idx] : 0;
    current = remainder * 10 + digit;
    const quotientDigit = Math.floor(current / source.divisor);
    const product = quotientDigit * source.divisor;
    remainder = current - product;
    const skipped = quotientDigit === 0 && quotientIndex === 0 && idx < integerDigitCount - 1;
    const shouldWrite = !skipped;

    if (shouldWrite) {
      quotientDigits[idx] = String(quotientDigit);
      quotientIndex += 1;
      const nextDigit = idx + 1 < dividendDigits.length ? dividendDigits[idx + 1] : idx + 1 === dividendDigits.length ? 0 : null;
      const currentLength = String(current).length;
      const remainderPadded = String(remainder).padStart(currentLength, "=");
      const remainderText =
        nextDigit == null
          ? String(remainder).padStart(currentLength, " ")
          : `${remainderPadded}${nextDigit}`;
      rows.push({
        current,
        quotientDigit,
        product,
        remainder,
        remainderText,
        start: idx - String(current).length + 1,
        resultStart: idx - currentLength + 1,
      });
    } else {
      quotientDigits[idx] = "=";
      detailLines.push({ current, quotientDigit, product, remainder, skipped });
    }

    if (shouldWrite) {
      detailLines.push({ current, quotientDigit, product, remainder, skipped: false });
    }

    steps.push({
      dividendUnits: source.dividendUnits,
      dividendPlaces: source.dividendPlaces,
      divisor: source.divisor,
      answerText: source.answerText,
      quotientDigits: [...quotientDigits],
      activeQuotientIndex: shouldWrite ? idx : null,
      rows: rows.map((row) => ({ ...row })),
      activeRow: shouldWrite ? rows.length - 1 : null,
      activeKind: skipped ? "skip" : "subtract",
    });

    current = remainder;
  }

  steps.push({
    dividendUnits: source.dividendUnits,
    dividendPlaces: source.dividendPlaces,
    divisor: source.divisor,
    answerText: source.answerText,
    quotientDigits: quotientDigits.map((digit) => (digit === "=" ? " " : digit)),
    activeQuotientIndex: null,
    rows: rows.map((row) => ({ ...row })),
    activeRow: null,
    activeKind: "final",
  });

  return { detailLines, steps };
}

function DigitCells({ digits, offset = 1, row = 1, highlightIndices = [], commaAfter = null }) {
  return (
    <>
      {digits.map((digit, idx) => {
        const visible = digit !== " " && digit !== "";
        const highlighted = highlightIndices.includes(idx);
        return (
          <span
            key={`${row}-${offset}-${idx}`}
            style={{
              gridColumn: offset + idx,
              gridRow: row,
              textAlign: "center",
              minWidth: "1.08ch",
              color: highlighted && visible ? "#f59e0b" : undefined,
              fontWeight: highlighted && visible ? 700 : undefined,
              position: "relative",
            }}
          >
            {visible ? digit : "\u00A0"}
            {commaAfter === idx ? (
              <span
                style={{
                  position: "absolute",
                  right: "-0.52ch",
                  bottom: "0.02em",
                  fontSize: "0.74em",
                  fontWeight: 700,
                }}
              >
                ,
              </span>
            ) : null}
          </span>
        );
      })}
    </>
  );
}

function LongDecimalDivisionBoard({ step }) {
  const divisorDigits = String(step.divisor).split("");
  const dividendDigits = String(step.dividendUnits).split("");
  const integerDigitCount = Math.max(1, dividendDigits.length - step.dividendPlaces);
  const quotientCommaAfter = step.answerText.includes(",") ? integerDigitCount - 1 : null;
  const appendedPlaces = step.quotientDigits.length - dividendDigits.length;
  const cols = Math.max(
    dividendDigits.length + Math.max(appendedPlaces, 0),
    step.quotientDigits.length,
    ...step.rows.flatMap((row) => [String(row.product).length, String(row.remainderText).length]),
    2
  );
  const rightOffset = cols + 2;
  const dividendCells = padDigits(String(step.dividendUnits), cols);
  const quotientCells = padDigits(step.quotientDigits.join(""), cols);
  const commaShift = Math.max(cols - dividendDigits.length, 0);
  const activeQuotientColumn =
    step.activeKind !== "final" && step.activeQuotientIndex != null
      ? [step.activeQuotientIndex + commaShift]
      : [];

  return (
    <WrittenBoard>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `1ch repeat(${cols}, 1.08ch) 1.2ch repeat(${Math.max(divisorDigits.length, 1)}, 1.08ch)`,
          columnGap: "0.18em",
          alignItems: "end",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          lineHeight: 1.25,
        }}
      >
        <DigitCells
          digits={quotientCells}
          offset={2}
          row={1}
          highlightIndices={activeQuotientColumn}
          commaAfter={quotientCommaAfter == null ? null : quotientCommaAfter + commaShift}
        />
        <div
          style={{
            gridColumn: `2 / ${cols + 2}`,
            gridRow: 1,
            alignSelf: "end",
            borderBottom: "1px solid var(--color-text)",
            height: 0,
          }}
        />
        <DigitCells
          digits={dividendCells}
          offset={2}
          row={2}
          commaAfter={step.dividendPlaces > 0 ? integerDigitCount - 1 + commaShift : null}
        />
        <span style={{ gridColumn: cols + 2, gridRow: 2 }}>:</span>
        <DigitCells digits={divisorDigits} offset={rightOffset + 1} row={2} />

        {step.rows.map((row, idx) => {
          const baseRow = 3 + idx * 2;
          const highlighted = step.activeRow === idx;
          const productCols = String(row.product).length;
          const productStart = row.start + String(row.current).length - productCols + commaShift;
          const remainderCells = divisionRemainderCells(row, commaShift);
          const firstDigitColumn = Math.max(productStart, 0) + 2;
          const signColumn = Math.max(firstDigitColumn - 1, 1);

          return (
            <React.Fragment key={idx}>
              <span style={{ gridColumn: signColumn, gridRow: baseRow, textAlign: "center" }}>-</span>
              <DigitCells
                digits={padDigits(row.product, productCols)}
                offset={firstDigitColumn}
                row={baseRow}
                highlightIndices={highlighted ? Array.from({ length: productCols }, (_, colIdx) => colIdx) : []}
              />
              <div
                style={{
                  gridColumn: `${firstDigitColumn} / ${firstDigitColumn + productCols}`,
                  gridRow: baseRow,
                  alignSelf: "end",
                  borderBottom: "1px solid var(--color-text)",
                  height: 0,
                }}
              />
              <DigitCells
                digits={remainderCells.digits}
                offset={remainderCells.offset}
                row={baseRow + 1}
                highlightIndices={
                  highlighted
                    ? Array.from({ length: remainderCells.digits.length }, (_, colIdx) => colIdx)
                    : []
                }
              />
            </React.Fragment>
          );
        })}
      </div>
    </WrittenBoard>
  );
}

function DecimalDivisionDetails({ lines, divisor }) {
  return (
    <ul>
      {lines.map((line, idx) => (
        <li key={idx}>
          {line.skipped ? (
            <>
              <InlineMath math={`${line.current}<${divisor}`} />, więc bierzemy następną cyfrę.
            </>
          ) : (
            <>
              W <InlineMath math={`${line.current}`} /> mieści się <InlineMath math={`${line.quotientDigit}`} /> razy{" "}
              <InlineMath math={`${divisor}`} />, bo{" "}
              <InlineMath math={`${line.quotientDigit}\\cdot ${divisor}=${line.product}`} />. Odejmujemy{" "}
              <InlineMath math={`${line.current}-${line.product}=${line.remainder}`} />.
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function DecimalWrittenDivision({ data }) {
  const shifted = shiftedDivisionData(data);
  const written = analyzeDecimalWrittenDivision(shifted);
  return (
    <>
      <p>
        <strong>Krok 1:</strong> Dzielną zapisujemy pod poziomą kreską, dzielnik po prawej stronie po dwukropku, a
        iloraz u góry. Gdy schodzimy za przecinek w dzielnej, przecinek pojawia się też w ilorazie.
      </p>
      <p>
        <strong>Krok 2:</strong> Liczymy kolejne części dzielnej, mnożymy przez dzielnik i odejmujemy pod odpowiednim
        rzędem wartości.
      </p>
      <DecimalDivisionDetails lines={written.detailLines} divisor={shifted.divisor} />
      <StepStrip
        alignItems="flex-start"
        steps={written.steps.map((step, idx) => (
          <LongDecimalDivisionBoard key={idx} step={step} />
        ))}
      />
    </>
  );
}

function makeData({ dividendUnits, dividendPlaces, divisorUnits, divisorPlaces, variant }) {
  const dividendText = formatDecimal(dividendUnits, dividendPlaces);
  const divisorText = formatDecimal(divisorUnits, divisorPlaces);
  const value = (dividendUnits / pow10(dividendPlaces)) / (divisorUnits / pow10(divisorPlaces));
  const commonPlaces = Math.max(decimalPlacesOf(dividendText), decimalPlacesOf(divisorText));
  const naturalDividend = Math.round((dividendUnits / pow10(dividendPlaces)) * pow10(commonPlaces));
  const naturalDivisor = Math.round((divisorUnits / pow10(divisorPlaces)) * pow10(commonPlaces));
  const answerText = formatDecimal(Math.round(value * 1000000), 6);

  return {
    dividendUnits,
    dividendPlaces,
    divisorUnits,
    divisorPlaces,
    dividendText,
    divisorText,
    naturalDividend,
    naturalDivisor,
    commonPlaces,
    answer: value,
    answerText,
    variant,
  };
}

function byNaturalSimple() {
  const divisor = pickOne([2, 4, 5, 8]);
  const resultPlaces = 2;
  const resultUnits = randInt(105, 999);
  const dividendUnits = resultUnits * divisor;
  return makeData({
    dividendUnits,
    dividendPlaces: resultPlaces,
    divisorUnits: divisor,
    divisorPlaces: 0,
    variant: "natural",
  });
}

function byNaturalWithZeros() {
  for (let i = 0; i < 400; i += 1) {
    const divisorUnits = pickOne([2, 4, 5, 8, 10, 20, 25]);
    const dividendPlaces = pickOne([1, 2, 3]);
    const answerPlaces = dividendPlaces + 1;
    const answerUnits = randInt(answerPlaces === 2 ? 12 : answerPlaces === 3 ? 105 : 1008, answerPlaces === 2 ? 98 : answerPlaces === 3 ? 987 : 9992);
    const dividendUnits = (answerUnits * divisorUnits) / 10;
    if (!Number.isInteger(dividendUnits) || dividendUnits % 10 === 0) continue;
    return makeData({
      dividendUnits,
      dividendPlaces,
      divisorUnits,
      divisorPlaces: 0,
      variant: "natural-zero",
    });
  }

  return makeData({
    dividendUnits: 117,
    dividendPlaces: 2,
    divisorUnits: 2,
    divisorPlaces: 0,
    variant: "natural-zero",
  });
}

function sameDecimalPlaces() {
  const places = 1;
  const divisorUnits = pickOne([2, 3, 4, 5, 6, 7, 8, 9]);
  const quotient = randInt(2, 9);
  return makeData({
    dividendUnits: divisorUnits * quotient,
    dividendPlaces: places,
    divisorUnits,
    divisorPlaces: places,
    variant: "shift-same",
  });
}

function appendZerosToDividend() {
  for (let i = 0; i < 300; i += 1) {
    const divisorPlaces = pickOne([2, 3]);
    const dividendPlaces = divisorPlaces - 1;
    const divisorUnits = divisorPlaces === 2 ? pickOne([5, 9, 15, 25, 35, 45, 75]) : pickOne([125, 225, 375, 625]);
    const quotient = randInt(2, 18);
    const dividendUnits = (divisorUnits * quotient) / 10;
    if (!Number.isInteger(dividendUnits) || dividendUnits % 10 === 0) continue;
    return makeData({
      dividendUnits,
      dividendPlaces,
      divisorUnits,
      divisorPlaces,
      variant: "shift-append-zero",
    });
  }

  return makeData({
    dividendUnits: 45,
    dividendPlaces: 1,
    divisorUnits: 9,
    divisorPlaces: 2,
    variant: "shift-append-zero",
  });
}

function decimalByDecimalFractionMethod() {
  const divisorPlaces = pickOne([1, 2]);
  const divisorUnits = divisorPlaces === 1 ? randInt(2, 9) : randInt(11, 29);
  const answerPlaces = pickOne([1, 2]);
  const answerUnits = randInt(answerPlaces === 1 ? 12 : 105, answerPlaces === 1 ? 98 : 986);
  const dividendPlaces = divisorPlaces + answerPlaces;
  const dividendUnits = divisorUnits * answerUnits;
  if (dividendUnits % 10 === 0) return decimalByDecimalFractionMethod();
  return makeData({
    dividendUnits,
    dividendPlaces,
    divisorUnits,
    divisorPlaces,
    variant: "fraction",
  });
}

function decimalByDecimalFractionHarder() {
  const divisorPlaces = pickOne([2, 3]);
  const divisorUnits = divisorPlaces === 2 ? randInt(31, 89) : randInt(115, 499);
  const answerPlaces = pickOne([0, 1, 2]);
  const answerUnits = answerPlaces === 0 ? randInt(2, 24) : randInt(answerPlaces === 1 ? 12 : 105, answerPlaces === 1 ? 99 : 999);
  const dividendPlaces = divisorPlaces + answerPlaces;
  const dividendUnits = divisorUnits * answerUnits;
  if (dividendUnits % 10 === 0 || dividendUnits > 99999) return decimalByDecimalFractionHarder();
  return makeData({
    dividendUnits,
    dividendPlaces,
    divisorUnits,
    divisorPlaces,
    variant: "fraction",
  });
}

function generateByVariant(variant) {
  if (variant === "natural") return byNaturalSimple();
  if (variant === "natural-zero") return byNaturalWithZeros();
  if (variant === "shift-same") return sameDecimalPlaces();
  if (variant === "shift-append-zero") return appendZerosToDividend();
  if (variant === "fraction-hard") return decimalByDecimalFractionHarder();
  return decimalByDecimalFractionMethod();
}

export function generateTask(options = {}) {
  return {
    type: "DecimalDivisionTask",
    data: generateByVariant(options.variant || "natural"),
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Oblicz:{" "}
        <InlineMath
          math={`${formatMathDecimal(data.dividendText)}:${formatMathDecimal(data.divisorText)}`}
        />
        .
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder="np. 6,9 lub 6.9"
    />
  );
}

export function checkAnswer(task, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - task.data.answer) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={formatMathDecimal(data.answerText)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

function renderNaturalExplanation(data) {
  const scale = pow10(data.dividendPlaces);
  return (
    <>
      <p>
        Dzielimy ułamek dziesiętny przez liczbę naturalną. Można myśleć o tym tak, że dzielimy liczbę bez
        przecinka, a potem ustawiamy przecinek w wyniku.
      </p>
      <p>
        <InlineMath
          math={`${formatMathDecimal(data.dividendText)}:${data.divisorUnits}=\\frac{${data.dividendUnits}}{${scale}}:${data.divisorUnits}`}
        />
      </p>
      <p>
        <InlineMath
          math={`\\frac{${data.dividendUnits}}{${scale}}:${data.divisorUnits}=\\frac{${data.dividendUnits}:${data.divisorUnits}}{${scale}}=${formatMathDecimal(data.answerText)}`}
        />
        .
      </p>
      <DecimalWrittenDivision data={data} />
    </>
  );
}

function renderNaturalZeroExplanation(data) {
  return (
    <>
      <p>
        Tu po przecinku trzeba zejść dalej niż w zapisie dzielnej. Dopisanie zera na końcu ułamka
        dziesiętnego nie zmienia jego wartości.
      </p>
      <p>
        <InlineMath
          math={`${formatMathDecimal(data.dividendText)}=${formatMathDecimal(
            formatDecimalFixed(data.dividendUnits * 10, data.dividendPlaces + 1)
          )}`}
        />
        , więc możemy kontynuować dzielenie.
      </p>
      <p>
        <InlineMath
          math={`${formatMathDecimal(data.dividendText)}:${data.divisorUnits}=${formatMathDecimal(data.answerText)}`}
        />
        .
      </p>
      <DecimalWrittenDivision data={data} />
    </>
  );
}

function renderShiftExplanation(data) {
  const scale = pow10(data.commonPlaces);
  return (
    <>
      <p>
        Przesuwamy przecinek w obu liczbach o {placesText(data.commonPlaces)} w prawo, czyli mnożymy dzielną i
        dzielnik przez <InlineMath math={`${scale}`} />. Wartość ilorazu się nie zmienia.
      </p>
      <p>
        <InlineMath
          math={`${formatMathDecimal(data.dividendText)}:${formatMathDecimal(data.divisorText)}=${data.naturalDividend}:${data.naturalDivisor}`}
        />
        .
      </p>
      <p>
        <InlineMath math={`${data.naturalDividend}:${data.naturalDivisor}=${formatMathDecimal(data.answerText)}`} />.
      </p>
      {naturalDivisionCheck(data)}
    </>
  );
}

function renderFractionExplanation(data) {
  const dividendDen = pow10(data.dividendPlaces);
  const divisorDen = pow10(data.divisorPlaces);
  const productFractions = [
    { numerator: data.dividendUnits, denominator: dividendDen },
    { numerator: divisorDen, denominator: data.divisorUnits },
  ];
  return (
    <>
      <p>
        Możemy też zamienić oba ułamki dziesiętne na ułamki zwykłe i dzielenie zastąpić mnożeniem przez
        odwrotność.
      </p>
      <p>
        <InlineMath
          math={`${formatMathDecimal(data.dividendText)}:${formatMathDecimal(data.divisorText)}
          =${fractionLatex(data.dividendUnits, dividendDen)}:${fractionLatex(data.divisorUnits, divisorDen)}
          =${fractionLatex(data.dividendUnits, dividendDen)}\\cdot ${fractionLatex(divisorDen, data.divisorUnits)}`}
        />
        .
      </p>
      <p>
        Skracamy wspólne czynniki w licznikach i mianownikach przed mnożeniem.
      </p>
      <CancellationProduct fractions={productFractions} />
      {data.variant === "fraction-hard" ? (
        <>
          <p>Ten sam iloraz można też policzyć pisemnie po przesunięciu przecinka tak, aby dzielnik był naturalny.</p>
          <DecimalWrittenDivision data={data} />
        </>
      ) : null}
    </>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      {data.variant === "natural" ? renderNaturalExplanation(data) : null}
      {data.variant === "natural-zero" ? renderNaturalZeroExplanation(data) : null}
      {data.variant === "shift-same" || data.variant === "shift-append-zero" ? renderShiftExplanation(data) : null}
      {data.variant === "fraction" || data.variant === "fraction-hard" ? renderFractionExplanation(data) : null}
      <p>
        <strong>Wynik:</strong> <InlineMath math={formatMathDecimal(data.answerText)} />
      </p>
    </div>
  );
}
